/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from "react";
import {useTypedSelector} from "helpers/reducers";
import {useTranslation} from "react-i18next";
import {ShopArticleItem} from "types/entities/ShopArticleItem";
import {appendQueryParams, currencyNumberFormat, translate} from "utils/utility";
import {shopArticlePrice, useShopItemsCartHook} from "utils/giftCardShopUtils";
import {ChevronDownIcon, ShoppingCartIcon, TrashIcon} from "@heroicons/react/24/solid";
import {OverlineText} from "pages/Front/components/TextStyles/OverlineText";
import {TAX} from "helpers/config";
import {RouteComponentProps, withRouter} from "react-router-dom";
import clsx from "clsx";
import {Tooltip} from "react-tooltip";

interface Params {}

interface GiftCardShoppingCartProps extends RouteComponentProps<Params> {
    withCheckoutButton: boolean;
    isNewStyle?: boolean;
}

const VoucherShoppingCart = (props: GiftCardShoppingCartProps) => {
    const itemsCart = useTypedSelector(state => state.shopState.itemsCart);
    const {removeFromCart} = useShopItemsCartHook();
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const [itemsCartOpen, setItemsCartOpen] = useState(false);
    const disabled = itemsCart?.shopArticleItems && itemsCart.shopArticleItems.length === 0;
    const {t} = useTranslation();

    const chooseHelper = () => {
        if (disabled) {
            return t("Please select amount of gift cards");
        }
    };

    const proceedToCheckout = () => {
        if (disabled) {
            return t("Please select amount of gift cards");
        }
        const url = "/booking/checkout?" + appendQueryParams(bookingFilter);
        props.history.replace(url);
    };

    return (
        <div
            className={clsx(
                "GiftCardShoppingCart relative flex flex-col sticky bottom-0 z-[11] shrink-0 sm:min-w-[360px] shadow-md bg-white overflow-hidden",
                props?.isNewStyle ? "rounded-xl" : "rounded-md"
            )}
        >
            <div className={"flex flex-col justify-between relative z-30 grow"}>
                <section className={"shopArticleItems px-4 py-2 sm:py-4"}>
                    <div className={"flex flex-row justify-between"} onClick={() => setItemsCartOpen(!itemsCartOpen)}>
                        {props?.isNewStyle ? (
                            <div className={"block uppercase tracking-wider font-bold text-lg"}>{t("Shopping cart")}</div>
                        ) : (
                            <OverlineText text={t("Shopping cart")} />
                        )}
                        <ChevronDownIcon className={clsx(itemsCartOpen ? "rotate-180" : "", "duration-500 origin-center transform-gpu w-5 h-5 sm:hidden")} />
                    </div>

                    <div
                        className={clsx(
                            itemsCartOpen ? "max-h-[70vh]" : "max-h-0 sm:max-h-[500px]",
                            "shopArticleItemsList divide-y divide-gray-200 overflow-y-auto"
                        )}
                    >
                        <div
                            className={clsx(
                                props.withCheckoutButton
                                    ? props?.isNewStyle
                                        ? "grid-cols-[1fr,74px,20px]"
                                        : "grid-cols-[36px,1fr,60px,20px]"
                                    : "grid-cols-[36px,1fr,80px]",
                                "grid items-center gap-2 my-2",
                                props?.isNewStyle ? "text-sm font-semibold" : "text-xs"
                            )}
                        >
                            {!props?.isNewStyle && <span>{t("Qty")}</span>}
                            <span>{t("Article")}</span>
                            <span>{t("Price")}</span>
                            {props.withCheckoutButton && <span></span>}
                        </div>
                        {itemsCart?.shopArticleItems &&
                            itemsCart.shopArticleItems.map((item: ShopArticleItem, index) => {
                                return (
                                    <div
                                        key={index}
                                        className={clsx(
                                            "ShopArticleItem py-1 text-sm grid w-full",
                                            props?.isNewStyle ? "text-sm" : "text-sm",
                                            props.withCheckoutButton
                                                ? props?.isNewStyle
                                                    ? "grid-cols-[1fr,74px,20px]"
                                                    : "grid-cols-[36px,1fr,60px,20px]"
                                                : "grid-cols-[36px,1fr,80px]",
                                            "items-center gap-2"
                                        )}
                                    >
                                        {!props?.isNewStyle && <b className={props?.isNewStyle ? "font-medium" : ""}>{item.quantity}x</b>}
                                        <span>{translate(item.article.article_name)}</span>
                                        <span className={props?.isNewStyle ? "text-sm" : "text-xs"}>{shopArticlePrice(item)}</span>
                                        {props.withCheckoutButton && (
                                            <button className={props?.isNewStyle ? "mr-0 ml-auto" : ""} type="button" onClick={() => removeFromCart(item)}>
                                                <TrashIcon className={clsx("w-4 h-4", props?.isNewStyle ? "text-red-500" : "text-gray-800")} />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        {props?.isNewStyle && ((itemsCart?.shopArticleItems && itemsCart?.shopArticleItems.length === 0) || !itemsCart?.shopArticleItems) && (
                            <div className={"text-sm text-center py-1 w-full"}>{t("Nothing has been added yet.")}</div>
                        )}
                    </div>
                </section>
                <section className={"w-full text-sm"}>
                    <Tooltip data-tooltip-float={true} id={"chooseHelp"} />

                    {itemsCart.shopArticleItemsCart && (
                        <div className={"shopArticleItemsTotalPrice bg-gray-300 px-4 py-2 grid grid-cols-[1fr,100px] gap-4"}>
                            <span>
                                {t("Total price")} ({t("incl_tax_rate", {tax_rate: TAX})})
                            </span>
                            <b className={"text-right"}>{currencyNumberFormat(itemsCart.shopArticleItemsCart.price)}</b>
                        </div>
                    )}
                    {props.withCheckoutButton && (
                        <button
                            className={clsx(
                                disabled && "bg-opacity-50 grayscale-[100%] cursor-not-allowed",
                                "bg-cta-800 text-contrast-800 px-6 py-2 sm:py-4 flex flex-row justify-between w-full"
                            )}
                            data-for={"chooseHelp"}
                            data-tip={chooseHelper()}
                            disabled={disabled}
                            type={"button"}
                            onClick={proceedToCheckout}
                        >
                            <b>{t("Proceed to checkout")}</b>
                            <ShoppingCartIcon className={"w-6 h-6 text-contrast-800"} />
                        </button>
                    )}
                </section>
            </div>
        </div>
    );
};

export default withRouter(VoucherShoppingCart);
